<!--suppress ALL -->
<script lang="ts">
import { DisplayOptions, Label, SelectionType } from '@/types/tower/Tower';
import { TYPE as CustomPolicyRectType } from './objects/custom_policy_rect/CustomPolicyRect';
import { DEFAULT_OBJECT_COLOR } from './objects/utils/common';

export default {
  name: 'TowerCanvas',
};

interface CanvasJSON {
  towerId?: string;
  programId?: number;
  version?: string;
  objects?: fabric.Object[];
  gridModeEnabled?: boolean;
  displayOptions?: DisplayOptions;
  snapToModeEnabled?: boolean;
  zoomLevel?: number;
  viewportTransform?: number[];
  customPolicies: ProgramPolicyItem[];
  renewalProcessed?: boolean;
}

const SNAP_THRESHOLD = 20;
const ROTATION_SNAP_THRESHOLD = 15;

const canvasToolKeyMaps = new Map<string, SelectionType>([
  ['h', 'pan'],
  ['1', 'pan'],
  ['v', 'selection'],
  ['1', 'selection'],
  ['r', 'labeled-rect'],
  ['2', 'labeled-rect'],
  ['3', 'triangle'],
  ['t', 'text'],
  ['4', 'text'],
  ['s', 'tower-scale'],
  ['5', 'tower-scale'],
]);

const canvasPropsToInclude = [
  'gridModeEnabled',
  'displayOptions',
  'snapToModeEnabled',
  'viewportTransform',
  'zoomLevel',
  'customPolicies',
  'renewalProcessed',
];
</script>

<script setup lang="ts">
import { watch, computed, inject, ref, onMounted, onBeforeUnmount, markRaw } from 'vue';
// load the custom fabric objects
import '@/types/tower/v2/CustomFabricClasses';
import { fabric } from 'fabric';
import CanvasUtils, { DEFAULT_GRID_SIZE, MIN_RECT_SIZE } from '@/utilities/tower/v2/Canvas';
import { StoreMutationType } from '@/types/StoreMutationType';
import {
  fontFamily,
  FONT_SIZES,
  isConfigEnabledForObjectType,
  TowerCanvasState,
  TowerContextMenuState,
  TowerObjectConfig,
  DEFAULT_DISPLAY_LABELS,
  CanvasChangeReason,
} from '@/types/tower/Tower';
import { MutationPayload, Store } from 'vuex';
import { v4 as uuidv4 } from 'uuid';
import { ProgramInfo, ProgramPolicy } from '@/types/Programs';
import { TYPE as LabeledRectType } from '@/views/tower/v2/objects/LabeledRect';
import { Type as PolicyRectType } from '@/views/tower/v2/objects/policy_rect/PolicyRect';
import { TYPE as LegendRectType } from '@/views/tower/v2/objects/LegendRect';
import { ProgramTower } from '@/types/tower/v2/Tower';
import { SaveTowerAction, towerService } from '@/services/v2/TowerService';
import { AppState, Commit } from '@/store';
import {
  ADD_POLICY,
  ADD_ALL_POLICIES,
  DELETE_TOWER,
  LOAD_CANVAS_JSON,
  REMOVE_POLICY,
  SAVE_TOWER,
  UNDO_TOWER,
  REDO_TOWER,
  UPDATE_POLICY,
  UPDATE_TOWER,
  customEvents,
  UPDATE_TOWER_CUSTOM_POLICY,
  REMOVE_CUSTOM_POLICY,
  type UpdateTowerCustomPolicyPayload,
  ADD_CUSTOM_POLICY,
  UPDATE_TOWER_VERSIONS,
  TOWER_SCALE_MODE_CHANGED,
  ChangeObjectLayerOptions,
  CHANGE_OBJECT_LAYER,
  UPDATE_POLICY_CHILDREN,
} from '@/store/CustomEvents';
import { DownloadTower, FileType } from '@/utilities/tower/v2/Download';
import { type ProgramPolicyItem } from '@/views/tower/v2/objects/ProgramsLibraryPanel';
import { useRoute, useRouter } from 'vue-router';
import { accountService, programService } from '@/services/api';
import { TowerScaleMode } from '@/views/tower/v2/objects/tower_scale/TowerScale';
import { TowerType } from '@/views/tower_dashboard/types/tower-dashboard';

const router = useRouter();
const route = useRoute();

// -------
// props
// -------
const props = defineProps<{
  canvasDOMWidth: number;
  canvasDOMHeight: number;
}>();

// ---------------
// reactive data
// ---------------
let canvas = markRaw({} as fabric.TowerCanvas);
const canvasJSON = ref<CanvasJSON>();
const isDown = ref<boolean>(false);
const startPoint = ref<{ x: number; y: number }>({ x: 0, y: 0 });
const activeRect = ref<fabric.Object>();
const clipboard = ref<fabric.Object>();
const lastClientX = ref<number>(0);
const lastClientY = ref<number>(0);

const isDragging = ref<boolean>(false);
const dragStartX = ref<number>(0);
const dragStartY = ref<number>(0);

const objectStack = ref<fabric.Object[]>([]);
const displayOptions = ref<DisplayOptions>();
const timeoutId = ref<number>();
const towerInstanceTimeout = ref<number>();
const store = inject<Store<AppState>>('store');

// reference to underlying html canvas element
const htmlCanvas = ref<HTMLCanvasElement | null>(null);

// -----
// data
// -----
const subscriptions: ({ (): void } | undefined)[] = [];
const eventHandlers: [string, Handler<any>][] = [];

// ----------
// computed
// ----------
const canvasState = computed<TowerCanvasState | undefined>(() => {
  return store?.state.towerCanvasState;
});

const snapToModeEnabled = computed<boolean | undefined>(() => {
  return store?.state.towerCanvasState.snapToModeEnabled;
});

// ----------
// watchers
// ----------
watch(canvasJSON, async (newCanvasJSON, oldCanvasJSON) => {
  const changeReason = store?.state.towerCanvasState.canvasChangeReason;
  if (JSON.stringify(newCanvasJSON) === JSON.stringify(oldCanvasJSON)) {
    // nothing changed, so ignore
    store?.commit(StoreMutationType.SET_CANVAS_CHANGE_REASON, undefined);
    return;
  }

  const changeReasonsToSkipSave: CanvasChangeReason[] = [
    'towerVersionCreated',
    'towerVersionDeleted',
    'towerVersionSelected',
    'canvasInitialLoad',
    'programChanged',
    'retentionContainerSelected',
  ];

  if (changeReason && changeReasonsToSkipSave.includes(changeReason)) {
    store?.commit(StoreMutationType.SET_CANVAS_CHANGE_REASON, undefined);
  } else {
    // AUTO SAVE LOGIC
    const currentTower = store?.state.towerCanvasState.currentTowerInstance;
    await saveTower(currentTower as ProgramTower, '');

    if (changeReason !== 'undoTowerChange' && changeReason !== 'redoTowerChange') {
      store?.commit(StoreMutationType.ADD_TOWER_HISTORY, currentTower);
    }
  }

  // reset canvas change reason
  store?.commit(StoreMutationType.SET_CANVAS_CHANGE_REASON, undefined);

  dispatchEvent(new CustomEvent(UPDATE_TOWER_VERSIONS));
});

watch(
  () => store?.state.towerCanvasState.isDeletedProgramMode,
  (isReadOnly) => {
    if (!canvas || !isReadOnly) return;
    canvas.selection = false;
    canvas.forEachObject((obj: fabric.Object) => {
      obj.selectable = false;
      obj.evented = false;
    });
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  },
);

watch(
  () => props.canvasDOMWidth,
  (val) => {
    canvas.setWidth(val);
  },
);

watch(
  () => props.canvasDOMHeight,
  (val) => {
    canvas.setHeight(val);
  },
);

// -----------------
// lifecycle hooks
// -----------------
onMounted(async () => {
  // TODO: add beforeDestroy() cleanup to unregister all the events
  //increase the cache size to prevent grid line blurriness
  fabric.perfLimitSizeTotal = 225000000;
  fabric.maxCacheSideLimit = 11000;

  canvas = new fabric.TowerCanvas('c', {
    enableRetinaScaling: false,
    renderOnAddRemove: false,
    stopContextMenu: true,
    fireRightClick: true,
    preserveObjectStacking: true,
  });

  // vuex store subscriptions
  subscriptions.push(store?.subscribe(handleConfigChange));
  subscriptions.push(store?.subscribe(handleEditPolicyRect));

  store?.commit(StoreMutationType.SHOW_TOWER_EXPORT_OPTIONS, false);

  // Load via towerId prop, if load failure we bump back to the tower dashboard
  let towerToLoad: ProgramTower;

  if (route.params.id && typeof route.params.id == 'string') {
    try {
      towerToLoad = await towerService.getTowerByTowerId(route.params.id);
    } catch (e) {
      console.error(e);
      await router.replace('/v2/tower');
      return;
    }
  } else {
    await router.replace('/v2/tower');
    return;
  }

  if (towerToLoad) {
    const accountId = towerToLoad.accountId;
    const programId = towerToLoad.programId;
    const accountName = towerToLoad.accountId ? (await accountService.findById(towerToLoad.accountId)).accountName : '';

    store?.commit(StoreMutationType.SET_ACCOUNT_ID, accountId);
    store?.commit(StoreMutationType.SET_PROGRAM_ID, programId);
    store?.commit(StoreMutationType.SET_ACCOUNT_NAME, accountName);
    store?.commit(StoreMutationType.SET_PROGRAM_NAME, towerToLoad.program.name);
    store?.commit(StoreMutationType.SET_PROGRAMS_PANEL_STATE, {
      show: true,
      tab: 'program',
    });

    store?.commit(StoreMutationType.SET_CANVAS_CHANGE_REASON, 'canvasInitialLoad');
    await loadCanvasJSON(towerToLoad);
    Commit(StoreMutationType.ADD_TOWER_HISTORY, towerToLoad);

    // Convert policies/policy rects to custom policies during the initial load of a renewal tower
    if (towerToLoad.towerTypeId === TowerType.Renewal && towerToLoad.renewalProgramId) {
      let programInfo: ProgramInfo | undefined;
      try {
        programInfo = await programService.getProgramInfoById(towerToLoad.renewalProgramId);
      } catch {
        // If the linked program cannot be fetched (e.g. deleted and backend returns 404),
        // treat the tower as read-only.
        store?.commit(StoreMutationType.SET_DELETED_PROGRAM_MODE, true);
      }

      if (programInfo && !store?.state.towerCanvasState.renewalProcessed) {
        await processRenewalTower(towerToLoad, programInfo);
      }
    } else {
      // set renewal processed flag false if not a renewal tower (in case of renewals converted to bound coverage)
      store?.commit(StoreMutationType.SET_RENEWAL_PROCESSED, false);
    }
  } else {
    store?.commit(StoreMutationType.SET_EDIT_MODE, false);
    store?.commit(StoreMutationType.SET_PROGRAMS_PANEL_STATE, { show: true });
  }

  if (!store?.state.towerCanvasState.zoomLevel) {
    store?.commit(StoreMutationType.SET_TOWER_CANVAS_ZOOM_LEVEL, CanvasUtils.DEFAULT_ZOOM);
  }
  handleZoom(store?.state.towerCanvasState.zoomLevel);

  //display labels
  displayOptions.value = store?.state.towerCanvasState.displayOptions;

  // canvas handlers
  canvas.on('mouse:down', canvasHandler(handleMouseDown));
  canvas.on('mouse:wheel', handleMouseWheel);
  canvas.on('mouse:move', canvasHandler(handleMouseMove));
  canvas.on('selection:created', canvasHandler(handleSelectionCreated));
  canvas.on('selection:updated', canvasHandler(handleSelectionUpdated));
  canvas.on('selection:cleared', canvasHandler(handleSelectionCleared));
  canvas.on('object:modified', canvasHandler(handleObjectModified));
  canvas.on('object:rotating', canvasHandler(handleObjectRotating));
  canvas.on('object:added', canvasHandler(handleObjectAdded));
  canvas.on('object:removed', canvasHandler(handleObjectRemoved));
  canvas.on('mouse:up', canvasHandler(handleMouseUp));
  canvas.on('object:moving', canvasHandler(handleObjectMoving));
  canvas.on('object:scaling', canvasHandler(handleObjectScaling));
  canvas.on('after:render', handleAfterRender);
  canvas.on('before:render', handleBeforeRender);

  registerEventListener('mousedown', canvasHandler(handlePanTriggerDown));
  registerEventListener('mouseup', canvasHandler(handlePanTriggerUp));
  registerEventListener('keydown', canvasHandler(handleKeydown));
  registerEventListener('copy', canvasHandler(handleCopy));
  registerEventListener('paste', canvasHandler(handlePaste));
  registerEventListener('contextmenu', canvasHandler(handleContextMenu));
  registerEventListener(ADD_POLICY, handleAddPolicy as EventListener);
  registerEventListener(ADD_CUSTOM_POLICY, handleAddCustomPolicy as EventListener);
  registerEventListener(ADD_ALL_POLICIES, handleAddAllPolicies as EventListener);
  registerEventListener(REMOVE_POLICY, handleRemovePolicy as EventListener);
  registerEventListener(REMOVE_CUSTOM_POLICY, handleRemoveCustomPolicy as EventListener);
  registerEventListener(UPDATE_POLICY, handleUpdatePolicy as EventListener);
  registerEventListener(UPDATE_POLICY_CHILDREN, handleUpdatePolicyChildren as EventListener);
  registerEventListener(SAVE_TOWER, handleSaveTower as EventListener);
  registerEventListener(UPDATE_TOWER, handleUpdateTower as EventListener);
  registerEventListener(LOAD_CANVAS_JSON, handleLoadCanvasJSON as EventListener);
  registerEventListener(DELETE_TOWER, handleDeleteTower as EventListener);
  registerEventListener(UNDO_TOWER, handleUndoTowerHistory as EventListener);
  registerEventListener(REDO_TOWER, handleRedoTowerHistory as EventListener);
  registerEventListener(UPDATE_TOWER_CUSTOM_POLICY, handleUpdateCustomRectPolicy as EventListener);
  registerEventListener(UPDATE_TOWER_VERSIONS, handleUpdateTowerVersions as EventListener);
  registerEventListener(TOWER_SCALE_MODE_CHANGED, handleTowerScaleModeChange as EventListener);
  registerEventListener(CHANGE_OBJECT_LAYER, handleUpdateObjectLayer as EventListener);

  // Update tower versions for current program
  dispatchEvent(new CustomEvent(UPDATE_TOWER_VERSIONS));
});

onBeforeUnmount(() => {
  // Unsubscribe from vuex to prevent duplicate calls
  subscriptions.forEach((unsubscribe) => {
    if (unsubscribe) {
      unsubscribe();
    }
  });

  // deregister all event listeners
  eventHandlers.forEach((eventHandler) => removeEventListener(eventHandler[0], eventHandler[1]));

  // clean up state
  store?.commit(StoreMutationType.SET_ACCOUNT_ID, undefined);
  store?.commit(StoreMutationType.SET_PROGRAM_ID, undefined);
  store?.commit(StoreMutationType.SET_ACCOUNT_NAME, undefined);
  store?.commit(StoreMutationType.SET_PROGRAM_NAME, undefined);
  store?.commit(StoreMutationType.SET_CURRENT_TOWER_INSTANCE, undefined);
  store?.commit(StoreMutationType.SET_DELETED_PROGRAM_MODE, false);
});

// ----------
// handlers
// ----------
/** General handler type **/
type Handler<T> = (opt: T) => void;

/**
 Wrapper handler that will disable functionality of the handler if certain state
 conditions are not met.
 **/
function canvasHandler<T>(handler: Handler<T>): Handler<T> {
  return (opts: T) => {
    if (
      store?.state.towerCanvasState.editDisplayOptionsState.show ||
      !store?.state.towerCanvasState.editMode ||
      store?.state.towerCanvasState.isDeletedProgramMode
    ) {
      return;
    }
    handler(opts);
  };
}

function handleConfigChange(mutation: MutationPayload): void {
  // Allow these non-editing operations even in read-only mode
  if (mutation.type === StoreMutationType.SET_TOWER_CANVAS_ZOOM_LEVEL) {
    handleZoom(mutation.payload);
    return;
  }
  if (mutation.type === StoreMutationType.TOWER_EXPORT_FILE_TYPE) {
    handleDownloadTower(mutation.payload);
    return;
  }

  if (store?.state.towerCanvasState.isDeletedProgramMode) return;

  if (mutation.type === StoreMutationType.SET_TOWER_CONFIG_CHANGE) {
    const active = canvas.getActiveObject();
    if (!active) return;

    const payload = mutation.payload as TowerObjectConfig;
    if (!payload) return;

    if (active.type === 'activeSelection') {
      (active as fabric.Group).forEachObject((o: fabric.Object) => {
        applyConfig(o, payload);
      });
    } else if (active instanceof fabric.Object) {
      applyConfig(active as fabric.Object, payload);

      // todo: what this do? still needed?
      while (objectStack.value.length > 0) {
        applyConfig(objectStack.value.pop() as fabric.Object, payload);
      }
    }

    canvas.requestRenderAll();
  } else if (mutation.type === StoreMutationType.DUPLICATE_FABRIC_OBJECT) {
    handleCopy();
    handlePaste();
  } else if (mutation.type === StoreMutationType.DELETE_FABRIC_OBJECT) {
    handleDelete();
  } else if (mutation.type === StoreMutationType.SET_GRID_MODE) {
    handleGridModeChange();
  } else if (mutation.type === StoreMutationType.SET_LEGEND_MODE) {
    handleLegendModeChange();
  } else if (mutation.type === StoreMutationType.SET_TOWER_DISPLAY_OPTIONS) {
    handleSetDisplayOptions();
    showInspector();
  } else if (mutation.type === StoreMutationType.SET_SNAP_TO_MODE) {
    handleSnapToModeChange();
  }
}

function handleEditPolicyRect(mutation: MutationPayload): void {
  // TODO: update to handle policy rect and labeled rect
  if (mutation.type === StoreMutationType.UPDATE_TOWER_POLICY_OBJECT) {
    const payload = mutation.payload as fabric.Object;

    const policyObject = canvas.getObjects().find((obj) => obj.id === payload.id);
    if (payload.policy && policyObject?.type === LabeledRectType) {
      (policyObject as fabric.LabeledRect).updateLabelFromPolicy();
      canvas.requestRenderAll();
    }
  }
}

function handleMouseDown(options: fabric.IEvent<MouseEvent>): void {
  if (!canvas || !canvasState.value) {
    return;
  }

  // canvas interaction (left click)
  if (options.button === 1) {
    // close context menu if visible
    if (store?.state.towerCanvasState.contextMenuState.show) {
      store?.commit(StoreMutationType.SET_TOWER_CONTEXT_MENU_STATE, { show: false });
    }

    // if left-click on an object, ignore
    if (options.target) return;
    switch (canvasState.value.selection) {
      case 'pan':
      case 'selection':
        break;
      case 'labeled-rect': {
        isDown.value = true;
        canvas.selection = false;
        let pointer = canvas.getPointer(options.e);
        const { gridSquareSize, gridModeEnabled } = canvasState.value;

        startPoint.value.x = gridModeEnabled ? Math.round(pointer.x / gridSquareSize) * gridSquareSize : pointer.x;
        startPoint.value.y = gridModeEnabled ? Math.round(pointer.y / gridSquareSize) * gridSquareSize : pointer.y;

        const rect = new fabric.LabeledRect({
          left: startPoint.value.x,
          top: startPoint.value.y,
          width: pointer.x - startPoint.value.x,
          height: pointer.y - startPoint.value.y,
          originX: 'left',
          originY: 'top',
          angle: 0,
          transparentCorners: false,
          canvas: canvas,
          lockScalingFlip: true,
        });
        rect.textStyle = 'sans-serif';
        rect.strokeStyle = 'solid';
        activeRect.value = rect;

        canvas.add(activeRect.value);
        activeRect.value.setCoords();
        break;
      }
    }
  }
}

function handlePanTriggerDown(event: MouseEvent): void {
  if (!canvas || !canvasState.value) {
    return;
  }

  // if left-click in pan mode, or middle click, or right click
  if ((canvasState.value?.selection === 'pan' && event.button === 0) || event.button === 1 || event.button === 2) {
    canvas.selection = false;

    isDragging.value = true;
    lastClientX.value = event.clientX;
    lastClientY.value = event.clientY;

    dragStartX.value = event.clientX;
    dragStartY.value = event.clientY;

    canvas.setCursor('grabbing');
  }
}

function handlePanTriggerUp(event: MouseEvent): void {
  if (!canvas || !canvasState.value) {
    return;
  }

  // if left-click in pan mode, or middle click, or right click
  if ((canvasState.value.selection === 'pan' && event.button === 0) || event.button === 1 || event.button === 2) {
    isDragging.value = false;
    canvas.selection = true;

    // Recalculate object interaction after Panning
    if (!canvas.viewportTransform) return;
    canvas.setViewportTransform(canvas.viewportTransform);
    canvas.getObjects().forEach((obj) => obj.setCoords());

    if (event.button === 1 || event.button === 2) {
      canvas.setCursor('');
    } else {
      canvas.setCursor('grab');
    }
  }
}

function handleMouseWheel(options: fabric.IEvent<WheelEvent>): void {
  if (!canvas || !store) {
    return;
  }

  // Canvas Zooming
  const delta = options.e.deltaY;
  let zoom = canvas.getZoom();
  zoom *= 0.999 ** delta;

  if (zoom > CanvasUtils.MAX_ZOOM) zoom = CanvasUtils.MAX_ZOOM;
  if (zoom < CanvasUtils.MIN_ZOOM) zoom = CanvasUtils.MIN_ZOOM;
  canvas.zoomToPoint({ x: options.e.offsetX, y: options.e.offsetY }, zoom);
  options.e.preventDefault();
  options.e.stopPropagation();
  canvas.getObjects().forEach((obj) => obj.setCoords());
  canvas.requestRenderAll();

  store.commit(StoreMutationType.SET_TOWER_CANVAS_ZOOM_LEVEL, zoom);
}

function handleMouseMove(options: fabric.IEvent<MouseEvent>): void {
  if (!canvas || !canvasState.value || !store) {
    return;
  }

  if (canvasState.value.selection === 'pan') {
    canvas.setCursor('grab');
  }

  // Canvas panning
  if (isDragging.value) {
    canvas.setCursor('grabbing');
    const event = options.e;

    if (canvas.viewportTransform) {
      const vpt = canvas.viewportTransform;
      vpt[4] += event.clientX - lastClientX.value;
      vpt[5] += event.clientY - lastClientY.value;
    }

    canvas.requestRenderAll();
    lastClientX.value = event.clientX;
    lastClientY.value = event.clientY;
    return;
  }

  if (options.target) return;

  switch (canvasState.value.selection) {
    case 'pan':
    case 'selection':
      break;
    case 'labeled-rect': {
      if (!isDown.value) return;
      let pointer = canvas.getPointer(options.e);
      const { gridSquareSize, gridModeEnabled } = canvasState.value;
      const x = gridModeEnabled ? Math.round(pointer.x / gridSquareSize) * gridSquareSize : pointer.x;
      const y = gridModeEnabled ? Math.round(pointer.y / gridSquareSize) * gridSquareSize : pointer.y;
      if (startPoint.value.x > x) {
        activeRect.value?.set({ left: Math.abs(x) });
      }
      if (startPoint.value.y > y) {
        activeRect.value?.set({ top: Math.abs(y) });
      }
      activeRect.value?.set({
        width: Math.abs(startPoint.value.x - x),
        height: Math.abs(startPoint.value.y - y),
      });
      canvas.requestRenderAll();
    }
  }
}

function handleSelectionCreated(): void {
  Commit(StoreMutationType.SET_ACTIVE_CANVAS_OBJECT, canvas.getActiveObject());
  objectStack.value = [];
  showInspector();
}

function handleSelectionUpdated(): void {
  Commit(StoreMutationType.SET_ACTIVE_CANVAS_OBJECT, canvas.getActiveObject());
  objectStack.value = [];
  store?.commit(StoreMutationType.SET_TOWER_SELECTED_OBJECT, undefined);
  showInspector();
}

function handleSelectionCleared(): void {
  Commit(StoreMutationType.SET_ACTIVE_CANVAS_OBJECT, undefined);
  objectStack.value = [];
  store?.commit(StoreMutationType.SET_TOWER_SELECTED_OBJECT, undefined);
}

function showInspector(): void {
  if (!canvas || !store) {
    return;
  }

  const objects = canvas.getActiveObjects();

  // show the inspector panel if 1 or more objects selected, otherwise hide
  if (objects.length > 0) {
    const active = objects[0];

    // don't want inspector panel opening for rect label or retention container
    if (active instanceof fabric.Textbox || active instanceof fabric.RetentionContainer) {
      return;
    }

    if (active instanceof fabric.LabeledRect) {
      objectStack.value.push(active);
    }

    // Calculate width/height for rectangle types
    const isRectType = active.type === 'policy-rect' || active.type === 'custom-policy-rect' || active.type === 'labeled-rect';
    const actualWidth = isRectType && active.width && active.scaleX ? active.width * active.scaleX : undefined;
    const actualHeight = isRectType && active.height && active.scaleY ? active.height * active.scaleY : undefined;

    Commit(StoreMutationType.SET_TOWER_SELECTED_OBJECT, {
      ids: objects.map((o) => o.id),
      types: objects.map((o) => o.type),
      opacity: active.get('opacity') || 1,
      fill: active.get('fill') || '',
      stroke: active.get('stroke') || '',
      strokeWidth: active.get('strokeWidth') || 0,
      strokeDashArray: active.get('strokeDashArray') || [],
      textStyle: active.textStyle || '',
      fontFamily: active.fontFamily || '',
      fontSize: (active as any).fontSize || FONT_SIZES[1],
      textColor: active instanceof fabric.Text ? active.fill : (active as any).textColor,
      strokeStyle: active.strokeStyle,
      background: '',
      scaleMode: active instanceof fabric.TowerScale ? active.scaleMode : undefined,
      coverageTypeEnabled: active instanceof fabric.PolicyRect ? active.coverageTypeEnabled : undefined,
      coverageContainerFill: active instanceof fabric.PolicyRect ? active.coverageContainerFill : undefined,
      coverageContainerStroke: active instanceof fabric.PolicyRect ? active.coverageContainerStroke : undefined,
      width: actualWidth,
      height: actualHeight,
    } as TowerObjectConfig);
  }
}

/**
 * Prevent custom rects from being scaled too small and bounce back to a sensible default size when
 * the min threshold has been exceeded.
 * @param options
 */
function handleObjectModified(options: fabric.IEvent): void {
  if (!canvas || !options.target) return;

  dispatchEvent(
    new CustomEvent(customEvents.objectModified, {
      detail: {
        event: options,
      },
    }),
  );

  // ignore the retention container, this is part of the policy rect and not considered a top level canvas object
  if (options.target instanceof fabric.RetentionContainer) return;

  // Sync Inspector if the modified object is currently selected and is a rectangle type
  const activeObject = canvas.getActiveObject();
  if (
    activeObject &&
    (activeObject === options.target ||
      (activeObject.type === 'activeSelection' && (activeObject as fabric.Group).getObjects().includes(options.target)))
  ) {
    const isRectType =
      options.target.type === 'policy-rect' ||
      options.target.type === 'custom-policy-rect' ||
      options.target.type === 'labeled-rect';

    if (isRectType) {
      // Update Inspector to reflect the new dimensions
      showInspector();
    }
  }
}

function handleObjectRotating(options: fabric.IEvent): void {
  if (!canvas || !options.target) return;

  const targetObject = options.target;
  const rotation = targetObject.angle;
  const center = targetObject.getCenterPoint();

  // Snap to 90-degree angle when within 15 degrees
  if (rotation && (rotation % 90 < ROTATION_SNAP_THRESHOLD || rotation % 90 > 90 - ROTATION_SNAP_THRESHOLD)) {
    const snappedAngle = Math.round(rotation / 90) * 90;

    targetObject.set({
      angle: snappedAngle,
    });

    // Set back to the original center point
    targetObject.setPositionByOrigin(center, 'center', 'center');
  }

  targetObject.setCoords(); // Update object's coordinates
}

function handleObjectAdded(e: fabric.IEvent): void {
  e.target?.on('editing:exited', () => {
    if (canvasState.value) store?.commit(StoreMutationType.SET_IS_EDITING, false);
  });

  e.target?.on('editing:entered', () => {
    if (canvasState.value) store?.commit(StoreMutationType.SET_IS_EDITING, true);
  });

  store?.commit(StoreMutationType.UPDATE_CANVAS_OBJECTS_ARRAY, canvas.getObjects());
}

function handleObjectRemoved(): void {
  store?.commit(StoreMutationType.UPDATE_CANVAS_OBJECTS_ARRAY, canvas.getObjects());
}

function handleMouseUp(options: fabric.IEvent): void {
  if (!canvas || !canvasState.value) {
    return;
  }

  checkObjectScalability();

  // show context menu (right-click, and not dragging)

  const mouseEvent = options.e as MouseEvent;
  if (mouseEvent.button === 2 && mouseEvent.clientX === dragStartX.value && mouseEvent.clientY === dragStartY.value) {
    if (options.target) {
      canvas.setActiveObject(options.target);
    }

    store?.commit(StoreMutationType.SET_TOWER_CONTEXT_MENU_STATE, {
      show: true,
      left: `${options.pointer?.x}px`,
      top: `${options.pointer?.y}px`,
      activeObjectId: options.target?.id,
      objectType: options.target?.type,
    } as TowerContextMenuState);
  }

  isDragging.value = false;
  dragStartX.value = 0;
  dragStartY.value = 0;

  // Recalculate object interaction after Panning
  if (!canvas.viewportTransform) return;
  canvas.setViewportTransform(canvas.viewportTransform);
  canvas.getObjects().forEach((obj) => obj.setCoords());
  canvas.selection = true;

  switch (canvasState.value.selection) {
    case 'selection': {
      if (options.target) {
        if (options.target instanceof fabric.PolicyRect) {
          Commit(StoreMutationType.SET_TOWER_EDITOR_VISIBLE, true);
        } else {
          Commit(StoreMutationType.SET_TOWER_EDITOR_VISIBLE, false);
        }
        return;
      } else {
        Commit(StoreMutationType.SET_TOWER_EDITOR_VISIBLE, false);
      }
      break;
    }
    case 'labeled-rect': {
      if (activeRect.value) {
        isDown.value = false;
        canvas.discardActiveObject();
        canvas.selection = true;
        activeRect.value.setCoords();
        activeRect.value.id = uuidv4();
        if (!activeRect.value.width || activeRect.value.width < DEFAULT_GRID_SIZE * 2) {
          activeRect.value.set('width', MIN_RECT_SIZE * 6);
        }
        if (!activeRect.value.height || activeRect.value.height < DEFAULT_GRID_SIZE * 2) {
          activeRect.value.set('height', MIN_RECT_SIZE * 3);
        }
        activeRect.value.setCoords();
        if (activeRect.value instanceof fabric.LabeledRect) {
          activeRect.value.resizeLabel();
        }
        canvas.renderAll();
        Commit(StoreMutationType.SET_TOWER_CANVAS_SELECTION_TYPE, 'selection');
      }
      break;
    }
    case 'custom-policy-rect': {
      Commit(StoreMutationType.SET_TOWER_CANVAS_SELECTION_TYPE, 'selection');
      break;
    }
    case 'text': {
      let pointer = canvas.getPointer(options.e);
      const textObj = new fabric.IText('text', {
        left: pointer.x,
        top: pointer.y,
        originX: 'left',
        originY: 'top',
        fill: 'black',
        fontFamily: fontFamily('sans-serif'),
        lockScalingFlip: true,
        lockSkewingX: true,
        lockSkewingY: true,
        fontSize: FONT_SIZES[2],
        textAlign: 'center',
      });
      textObj.textStyle = 'sans-serif';

      canvas.add(textObj);
      canvas.renderAll();
      Commit(StoreMutationType.SET_TOWER_CANVAS_SELECTION_TYPE, 'selection');
      break;
    }
    case 'triangle': {
      let pointer = canvas.getPointer(options.e);
      canvas.add(
        new fabric.Triangle({
          left: pointer.x,
          top: pointer.y,
          originX: 'left',
          originY: 'top',
          fill: DEFAULT_OBJECT_COLOR,
          stroke: '#61BABA',
          strokeWidth: 0,
          strokeDashArray: [1, 0],
          transparentCorners: false,
          strokeUniform: true,
        }),
      );

      canvas.renderAll();
      Commit(StoreMutationType.SET_TOWER_CANVAS_SELECTION_TYPE, 'selection');
      break;
    }
    case 'tower-scale': {
      const pointer = canvas.getPointer(options.e);
      const towerScaleObj = new fabric.TowerScale({
        id: uuidv4(),
        left: pointer.x,
        top: pointer.y - 100,
        canvas: canvas,
      });
      canvas.add(towerScaleObj);
      canvas.renderAll();
      Commit(StoreMutationType.SET_TOWER_CANVAS_SELECTION_TYPE, 'selection');
      break;
    }
  }
}

function handleObjectMoving(options: fabric.IEvent): void {
  if (!canvas || !canvasState.value || !options.target) return;

  if (canvasState.value?.selection === 'pan') {
    return;
  }

  // grid mode logic
  if (canvasState.value.gridModeEnabled) {
    const grid = canvasState.value.gridSquareSize,
      left = options?.target?.left,
      top = options?.target?.top;

    if (left && top) {
      options?.target?.set({
        left: Math.round(left / grid) * grid,
        top: Math.round(top / grid) * grid,
      });
    }
  }

  if (snapToModeEnabled.value) applyMovingSnapToLogic(canvas, options.target);

  canvas.requestRenderAll();
}

function handleObjectScaling(options: fabric.IEvent): void {
  if (!options.target || !canvasState.value) return;

  if (options.target instanceof fabric.Group) {
    options.target._objects.forEach((obj) => {
      obj.fire('scaling', options);
    });
  }

  if (canvasState.value.gridModeEnabled) {
    const target = options.target;

    if (
      target?.width === undefined ||
      target?.height === undefined ||
      target?.scaleX === undefined ||
      target?.scaleY === undefined ||
      target.top === undefined ||
      target.left === undefined
    ) {
      return;
    }

    const w = target.width * target.scaleX;
    const h = target.height * target.scaleY;

    const grid = canvasState.value.gridSquareSize;

    const snap = {
      // Closest snapping points
      top: Math.round(target.top / grid) * grid,
      left: Math.round(target.left / grid) * grid,
      bottom: Math.round((target.top + h) / grid) * grid,
      right: Math.round((target.left + w) / grid) * grid,
    };

    const threshold = grid / 2;

    const dist = {
      // Distance from snapping points
      top: Math.abs(snap.top - target.top),
      left: Math.abs(snap.left - target.left),
      bottom: Math.abs(snap.bottom - target.top - h),
      right: Math.abs(snap.right - target.left - w),
    };

    const attrs = {
      top: target.top,
      left: target.left,
      scaleY: target.scaleY,
      scaleX: target.scaleX,
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    switch (target.__corner) {
      case 'tl':
        if (dist.left < threshold) {
          attrs.scaleX = (w - (snap.left - target.left)) / target.width;
          attrs.left = snap.left;
        }
        if (dist.top < threshold) {
          attrs.scaleY = (h - (snap.top - target.top)) / target.height;
          attrs.top = snap.top;
        }
        break;
      case 'mt':
        if (dist.top < threshold) {
          attrs.scaleY = (h - (snap.top - target.top)) / target.height;
          attrs.top = snap.top;
        }
        break;
      case 'tr':
        if (dist.top < threshold) {
          attrs.scaleY = (h - (snap.top - target.top)) / target.height;
          attrs.top = snap.top;
        }
        if (dist.right < threshold) attrs.scaleX = (snap.right - target.left - (target.strokeWidth || 0)) / target.width;
        break;
      case 'ml':
        if (dist.left < threshold) {
          attrs.scaleX = (w - (snap.left - target.left)) / target.width;
          attrs.left = snap.left;
        }
        break;
      case 'mr':
        if (dist.right < threshold) attrs.scaleX = (snap.right - target.left - (target.strokeWidth || 0)) / target.width;
        break;
      case 'bl':
        if (dist.bottom < threshold) attrs.scaleY = (snap.bottom - target.top - (target.strokeWidth || 0)) / target.height;
        if (dist.left < threshold) {
          attrs.scaleX = (w - (snap.left - target.left)) / target.width;
          attrs.left = snap.left;
        }
        break;
      case 'mb':
        if (dist.bottom < threshold) attrs.scaleY = (snap.bottom - target.top - (target.strokeWidth || 0)) / target.height;
        break;
      case 'br':
        if (dist.bottom < threshold) attrs.scaleY = (snap.bottom - target.top - (target.strokeWidth || 0)) / target.height;
        if (dist.right < threshold) attrs.scaleX = (snap.right - target.left - (target.strokeWidth || 0)) / target.width;
        break;
    }
    target.set(attrs);
  }

  if (snapToModeEnabled.value) {
    const snapper = options.target;
    const transform = options.transform;

    if (transform) {
      const scalingCorner = transform.corner;
      applyScalingSnapToLogic(canvas, snapper, scalingCorner);
    }
  }
}

function handleAfterRender(): void {
  if (!canvas) return;

  if (timeoutId.value) window.clearTimeout(timeoutId.value);
  timeoutId.value = window.setTimeout(() => {
    canvasJSON.value = {
      ...canvas.toDatalessJSON(),
      gridModeEnabled: store?.state.towerCanvasState.gridModeEnabled,
      snapToModeEnabled: store?.state.towerCanvasState.snapToModeEnabled,
      zoomLevel: store?.state.towerCanvasState.zoomLevel,
      towerId: store?.state.towerCanvasState.currentTowerInstance?.towerId,
      programId: store?.state.towerCanvasState.programId,
      viewportTransform: [...(canvas.viewportTransform ?? [])],
      customPolicies: store?.state.towerCanvasState.customPolicies ?? [],
      renewalProcessed: store?.state.towerCanvasState.renewalProcessed ?? false,
    };
  }, 450);

  canvas.setGridMode(store?.state.towerCanvasState.gridModeEnabled ?? false);
  canvas.setSnapToMode(store?.state.towerCanvasState.snapToModeEnabled ?? true);
  canvas.setZoomLevel(store?.state.towerCanvasState.zoomLevel ?? CanvasUtils.DEFAULT_ZOOM);
  canvas.setDisplayOptions(store?.state.towerCanvasState.displayOptions ?? ({} as DisplayOptions));
  canvas.setCustomPolicies(store?.state.towerCanvasState.customPolicies ?? []);
  canvas.setRenewalProcessed(store?.state.towerCanvasState.renewalProcessed ?? false);
  canvas.setViewportTransform([...(canvas.viewportTransform ?? [])]);

  if (towerInstanceTimeout.value) window.clearTimeout(towerInstanceTimeout.value);
  towerInstanceTimeout.value = window.setTimeout(() => {
    const currentTower = { ...store?.state.towerCanvasState.currentTowerInstance };
    const cJSON = canvas.toJSON(canvasPropsToInclude) || ({} as CanvasJSON);
    currentTower.towerObject = JSON.stringify(cJSON);
    currentTower.programId = store?.state.towerCanvasState.programId;
    currentTower.accountId = store?.state.towerCanvasState.accountId;
    store?.commit(StoreMutationType.SET_CURRENT_TOWER_INSTANCE, currentTower);
  }, 40);
}

function handleBeforeRender(): void {
  if (store?.state.towerCanvasState.gridModeEnabled) {
    drawGrid();
  }
}

// Backspace to delete current active object
async function handleKeydown(e: KeyboardEvent): Promise<void> {
  if (!canvas) return;
  if (canvasState.value?.isEditing) return;

  // Escape key sets selection to "selection" and deselects all objects
  if (e.key === 'Escape') {
    if (canvas.getActiveObject()) {
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      return;
    }

    if (canvasState.value?.selection === 'selection') {
      Commit(StoreMutationType.SET_TOWER_CANVAS_SELECTION_TYPE, 'pan');
    } else {
      Commit(StoreMutationType.SET_TOWER_CANVAS_SELECTION_TYPE, 'selection');
    }
    Commit(StoreMutationType.SET_TOWER_EDITOR_VISIBLE, false);
  }

  // Delete a Tower
  if (e.key === 'Backspace' || e.key === 'Delete') {
    // Don't delete if width/height inputs in Inspector are focused
    const activeElement = document.activeElement;
    if (
      activeElement &&
      activeElement.tagName === 'INPUT' &&
      (activeElement as HTMLInputElement).type === 'number' &&
      activeElement.closest('#object-style-inspector')
    ) {
      return;
    }
    handleDelete();
  }

  // tower canvas tool selection
  if (canvasToolKeyMaps.has(e.key)) {
    Commit(StoreMutationType.SET_TOWER_CANVAS_SELECTION_TYPE, canvasToolKeyMaps.get(e.key));
    return;
  }

  // keys with modifiers (Windows, Linux, and Mac)
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('windows') || userAgent.includes('linux')) {
    // Undo / Redo
    if (e.ctrlKey && e.key === 'z') {
      if (e.shiftKey) {
        await redoTowerHistory();
      } else {
        await undoTowerHistory();
      }
    }

    // Select all fabric objects in canvas
    if (e.ctrlKey && e.key === 'a') {
      e.preventDefault();
      selectAllCanvasObjects();
    }
  } else if (userAgent.includes('macintosh')) {
    // Redo / Redo
    if (e.metaKey && e.key === 'z') {
      if (e.shiftKey) {
        await redoTowerHistory();
      } else {
        await undoTowerHistory();
      }
    }

    // Select all fabric objects in canvas
    if (e.metaKey && e.key === 'a') {
      e.preventDefault();
      selectAllCanvasObjects();
    }
  }

  // toggle programs panel
  if (e.key === 'p') {
    Commit(StoreMutationType.SET_PROGRAMS_PANEL_STATE, {
      show: !store?.state.towerCanvasState.programsPanelState.show,
    });
  }

  // (DEV ONLY) Print out the current canvas
  if (e.key === ' ') {
    console.log('handleKeydown: current canvas');
    window.canvas = canvas;
    console.log(JSON.parse(JSON.stringify(canvas.toJSON(canvasPropsToInclude))));
  }
}

async function redoTowerHistory(): Promise<void> {
  if (store?.state.towerCanvasState.currentTowerHistoryIndex == undefined) {
    return;
  }

  if (store?.state.towerCanvasState.currentTowerHistoryIndex === store?.state.towerCanvasState.towerHistoryStack.length) {
    return;
  }
  Commit(StoreMutationType.REDO_TOWER_HISTORY);
  try {
    Commit(StoreMutationType.SET_CANVAS_CHANGE_REASON, 'redoTowerChange');
    const tower = store.state.towerCanvasState.towerHistoryStack[store.state.towerCanvasState.currentTowerHistoryIndex];
    await loadCanvasJSON({ ...tower });
  } catch (e) {
    console.error(e);
    Commit(StoreMutationType.SET_CANVAS_CHANGE_REASON, undefined);
  }
}

async function undoTowerHistory(): Promise<void> {
  if (!store?.state.towerCanvasState.currentTowerHistoryIndex) {
    return;
  }

  Commit(StoreMutationType.UNDO_TOWER_HISTORY);
  try {
    Commit(StoreMutationType.SET_CANVAS_CHANGE_REASON, 'undoTowerChange');
    const tower = store.state.towerCanvasState.towerHistoryStack[store.state.towerCanvasState.currentTowerHistoryIndex];
    await loadCanvasJSON({ ...tower });
  } catch (e) {
    console.error(e);
    Commit(StoreMutationType.SET_CANVAS_CHANGE_REASON, undefined);
  }
}

function selectAllCanvasObjects(): void {
  if (!canvas) return;
  const canvasObjects = canvas.getObjects();
  canvas.discardActiveObject();
  canvas.setActiveObject(new fabric.ActiveSelection(canvasObjects, { canvas: canvas }));
  checkObjectScalability();
  canvas.requestRenderAll();
}

function checkObjectScalability() {
  const activeObject = canvas.getActiveObject();
  const objectsToSkipScaling = ['tower-scale'];

  if (activeObject && activeObject instanceof fabric.ActiveSelection) {
    const allowScaling = !activeObject.getObjects().some((o) => objectsToSkipScaling.includes(o.type ?? ''));

    for (const controlKey of ['mtr', 'tl', 'tr', 'mr', 'br', 'bl', 'ml', 'mb', 'mt']) {
      activeObject.setControlVisible(controlKey, allowScaling);
    }
  }
}

function handleDelete(): void {
  if (!canvas) return;

  const activeObject = canvas.getActiveObject();

  if (activeObject) {
    // Don't delete while inside text field
    if ((activeObject.type === 'i-text' || activeObject.type === 'textbox') && (activeObject as fabric.Textbox).isEditing) {
      return;
    }

    if (activeObject.type === 'activeSelection') {
      (activeObject as fabric.Group).forEachObject((obj) => {
        if (obj instanceof fabric.LegendRect) {
          store?.commit(StoreMutationType.SET_LEGEND_MODE, false);
        }

        canvas.remove(obj);
      });
    } else {
      if (activeObject instanceof fabric.LegendRect) {
        store?.commit(StoreMutationType.SET_LEGEND_MODE, false);
      }
      canvas.remove(activeObject);
    }

    canvas.discardActiveObject();
    canvas.requestRenderAll();
  }
}

function handleCopy(): void {
  if (!canvas) return;
  if (canvasState.value?.isEditing) return;

  const activeObj = canvas.getActiveObject();

  // PolicyRects and LegendRects should not be able to be copied
  if (activeObj?.type === PolicyRectType || activeObj?.type === LegendRectType) return;

  if (!activeObj || (['i-text', 'textbox'].find((t) => t === activeObj.type) && (activeObj as fabric.Textbox).isEditing)) {
    return;
  }

  activeObj.clone(
    (clone: fabric.Object) => {
      if (clone.type === 'activeSelection') {
        const group = activeObj as fabric.Group;
        const clonedGroup = clone as fabric.Group;
        clonedGroup.forEachObject((o: fabric.Object) => {
          clonedGroup.remove(o);
        });

        group.forEachObject((o: fabric.Object) => {
          if (![PolicyRectType, LegendRectType].includes(o.type ?? '')) {
            o.clone((c: fabric.Object) => {
              if (c.id) {
                c.policy = getTowerPolicyObjectById(c.id)?.policy;
              }
              clonedGroup.add(c);
            });
          }
        });
      }
      if (clone.id) {
        clone.policy = getTowerPolicyObjectById(clone.id)?.policy;
      }
      clipboard.value = clone;
    },
    ['textStyle'],
  );
}

function handlePaste(): void {
  if (!canvas || !clipboard.value) return;
  if (canvasState.value?.isEditing) return;

  const activeObj = canvas.getActiveObject();

  if (activeObj && (activeObj.type === 'i-text' || activeObj.type === 'textbox') && (activeObj as fabric.Textbox).isEditing) {
    return;
  }

  clipboard.value.clone(
    (clonedObj: fabric.Object) => {
      canvas.discardActiveObject();
      clonedObj.set({
        left: clonedObj.left && clonedObj.left + 10,
        top: clonedObj.top && clonedObj.top + 10,
        evented: true,
      });

      if (clonedObj.type === 'activeSelection') {
        // active selection needs a reference to the canvas.
        clonedObj.canvas = canvas;

        const clonedGroup = clonedObj as fabric.Group;
        clonedGroup.forEachObject((o: fabric.Object) => {
          clonedGroup.remove(o);
        });

        (clipboard.value as fabric.Group).forEachObject((o: fabric.Object) => {
          o.clone((c: fabric.Object) => {
            clonedGroup.add(c);
            if (c.id) {
              c.id = uuidv4();
              c.policy = { ...o.policy } as ProgramPolicyItem;
            }
            canvas.add(c);
            // need to assign id after adding to canvas to guarantee uniqueness across custom policy rects
            if (c.type === CustomPolicyRectType && c.policy) {
              c.policy.id = getNewCustomPolicyId();
            }
          });
        });

        // this should solve the unselectability
        clonedObj.setCoords();
      } else {
        clonedObj.set('opacity', clipboard.value?.get('opacity'));
        clonedObj.set('fill', clipboard.value?.get('fill'));
        if (clipboard.value?.id) {
          clonedObj.id = uuidv4();
          if (clonedObj.type === CustomPolicyRectType && clipboard.value.policy) {
            clonedObj.policy = { ...clipboard.value.policy } as ProgramPolicyItem;
            clonedObj.policy.id = getNewCustomPolicyId();
          }
        }
        canvas.add(clonedObj);
      }
      if (clipboard.value?.top) clipboard.value.top += 10;
      if (clipboard.value?.left) clipboard.value.left += 10;
      canvas.setActiveObject(clonedObj);
      canvas.requestRenderAll();
    },
    ['textStyle'],
  );
}

function handleSetDisplayOptions(): void {
  if (!canvas) return;

  const displayOptions = store?.state.towerCanvasState.displayOptions;
  if (!displayOptions) return;

  canvas.forEachObject((obj: fabric.Object) => {
    if (obj instanceof fabric.PolicyRect) {
      const displayOptions = store?.state.towerCanvasState.displayOptions;
      if (displayOptions) {
        obj.setDisplayLabels(displayOptions);
      }
    } else if (obj instanceof fabric.LegendRect) {
      setLegendLabel(obj);
    } else if (obj instanceof fabric.LabeledRect) {
      (obj as fabric.LabeledRect).updateLabelFromPolicy();
    }
  });
  canvas.requestRenderAll();
}

function offsetIfNeeded(obj: fabric.Object): void {
  const objects = (canvas.getObjects() || []).filter((o) => o !== obj);
  const lefts = objects.map((o) => o.left);
  const tops = objects.map((o) => o.top);

  if (lefts.includes(obj.left) || tops.includes(obj.top)) {
    if (obj.left && obj.top) {
      obj.left += 10;
      obj.top += 10;
      offsetIfNeeded(obj);
    }
  }
}

function handleAddPolicy(e: CustomEvent): void {
  if (!canvas) return;

  const detail = e.detail;
  const policyRect = new fabric.PolicyRect({
    id: uuidv4(),
    policy: detail.policy,
    policyOverride: detail.policyOverride,
    lockScalingFlip: true,
    fontSize: FONT_SIZES[1],
  });
  canvas.add(policyRect);
  canvas.viewportCenterObject(policyRect);
  policyRect.setCoords();
  offsetIfNeeded(policyRect);
  canvas.setActiveObject(policyRect);
  canvas.requestRenderAll();
}

function handleAddCustomPolicy(e: CustomEvent): void {
  if (!canvas) return;

  const detail = e.detail;
  const policyRect = new fabric.CustomPolicyRect({
    id: uuidv4(),
    policy: detail?.policy ? detail.policy : ({ id: getNewCustomPolicyId() } as ProgramPolicyItem),
    lockScalingFlip: true,
    fontSize: FONT_SIZES[1],
  });
  canvas.add(policyRect);
  canvas.viewportCenterObject(policyRect);
  policyRect.setCoords();
  offsetIfNeeded(policyRect);
  canvas.setActiveObject(policyRect);
  canvas.requestRenderAll();
}

function getNewCustomPolicyId(): number {
  //need to make sure the policy id is unique from both the stored policies and the custom policy rects
  const customRectPolicyIds = (canvas?.getObjects().filter((o) => o.type === 'custom-policy-rect') ?? []).map((o) => o.policy?.id ?? 0);
  let storedCustomPolicyIds: number[] = [];

  const customProgramPolicies = store?.state.towerCanvasState.customPolicies;
  if (customProgramPolicies) {
    storedCustomPolicyIds = customProgramPolicies.map((o: ProgramPolicyItem) => o.id ?? 0);
  }

  const customTowersIds = [...customRectPolicyIds, ...storedCustomPolicyIds].sort((a, b) => b - a);

  if (customTowersIds.length > 0) {
    return customTowersIds[0] + 1;
  }
  return 1;
}

function handleAddAllPolicies(e: CustomEvent): void {
  if (!canvas) return;

  const policies = [...e.detail.policies] as ProgramPolicyItem[];
  let topOffset = 0;
  let startTop = 0;

  // Sort by attachment descending, then excess before primary
  policies.sort((a, b) => {
    return (b.attachmentPointValue ?? 0) - (a.attachmentPointValue ?? 0);
  });
  policies.sort((a, b) => {
    if (a.p_e_flag !== b.p_e_flag) {
      if (a.p_e_flag === 'Primary') {
        return 1;
      } else if (b.p_e_flag === 'Primary') {
        return -1;
      }
    }
    return 0;
  });

  for (let i = 0; i < policies.length; i++) {
    // create the new rect and add to the canvas
    const policyRect = new fabric.PolicyRect({
      id: uuidv4(),
      policy: policies[i],
      lockScalingFlip: true,
      fontSize: FONT_SIZES[1],
      objectCaching: false,
    });
    canvas.add(policyRect);

    // for the first added policy, center and use as the base ref for all other policies added
    if (i === 0) {
      canvas.viewportCenterObject(policyRect);
      startTop = policyRect.top ?? 0;
    } else {
      canvas.viewportCenterObjectH(policyRect);
    }

    policyRect.set({
      top: startTop + topOffset,
    });
    topOffset += policyRect.height ?? 0;

    // Pan to the last policy added
    if (i === policies.length - 1) {
      canvas.setActiveObject(policyRect);
      panCanvasToObject(policyRect);
    }
  }

  canvas.getObjects().forEach((obj) => obj.setCoords());
  canvas.requestRenderAll();
}

function handleRemovePolicy(e: CustomEvent): void {
  if (!canvas) return;

  const policy: ProgramPolicy = e.detail.policy;

  const policyRect = canvas
    .getObjects()
    .filter((o) => o.type === 'policy-rect')
    .find(
      (o) =>
        (o as fabric.PolicyRect).getPolicyData().id === policy.id && (o as fabric.PolicyRect).getPolicyData().policyLineId === policy.policyLineId,
    );

  if (policyRect) {
    canvas.remove(policyRect);
    canvas.requestRenderAll();
  }
}

function handleRemoveCustomPolicy(e: CustomEvent): void {
  if (!canvas) return;

  const policy: ProgramPolicyItem | undefined = e.detail.policy;
  if (policy) {
    const policyRect = canvas
      .getObjects()
      .filter((o) => o.type === 'custom-policy-rect')
      .find((o) => (o as fabric.CustomPolicyRect).policy?.id === policy.id);

    if (policyRect) {
      canvas.remove(policyRect);
    }
  }

  // always render here to update canvasJSON with the current custom policies
  canvas.requestRenderAll();
}

function handleUpdatePolicy(e: CustomEvent<{ policy: ProgramPolicy }>): void {
  if (!canvas) return;

  const policy: ProgramPolicy = e.detail.policy;

  const policyRect = canvas
    .getObjects()
    .filter((o) => o instanceof fabric.PolicyRect)
    .find((o) => {
      const policyRectPolicy = (o as fabric.PolicyRect).getPolicyData();
      return policyRectPolicy.id === policy.id && policyRectPolicy.policyLineId === policy.policyLineId;
    }) as fabric.PolicyRect;

  if (policyRect) {
    policyRect.updatePolicyData(policy);
    store?.commit(StoreMutationType.UPDATE_CANVAS_OBJECTS_ARRAY, canvas.getObjects());
    canvas.requestRenderAll();
  }

  dispatchEvent(
    new CustomEvent(customEvents.syncTowerScalePolicies, {
      detail: {
        policy: policy,
      },
    }),
  );
}

function handleUpdatePolicyChildren(e: CustomEvent<{ policyId: number; p_e_flag?: string }>) {
  if (!canvas) return;

  canvas
    .getObjects()
    .filter((o) => o instanceof fabric.PolicyRect)
    .filter((p) => p.policy?.id === e.detail.policyId && p.policy.isChildPolicy)
    .forEach((p) => {
      if (p.policy && e.detail.p_e_flag) {
        p.policy.p_e_flag = e.detail.p_e_flag;
        p.updatePolicyData(p.policy);
      }
    });

  store?.commit(StoreMutationType.UPDATE_CANVAS_OBJECTS_ARRAY, canvas.getObjects());
  canvas.requestRenderAll();
  dispatchEvent(new CustomEvent(customEvents.syncTowerLibraryPane));
}

function handleSaveTower(e: CustomEvent<{ tower: ProgramTower; action?: SaveTowerAction }>): void {
  if (!canvas) return;
  if (store?.state.towerCanvasState.isDeletedProgramMode) return;

  (async () => {
    await towerService.saveTower({ tower: e.detail.tower, action: e.detail.action });
  })();
}

function handleUpdateTower(e: CustomEvent<{ tower: ProgramTower }>): void {
  if (!canvas) return;

  (async () => {
    await updateTower(e.detail.tower);
  })();
}

function handleLoadCanvasJSON(e: CustomEvent<{ tower: ProgramTower; resetTowerHistory: boolean }>): void {
  loadCanvasJSON(e.detail.tower, e.detail.resetTowerHistory);
}

function handleDeleteTower(e: CustomEvent<{ tower: ProgramTower }>): void {
  if (!canvas) return;

  (async () => {
    deleteTower(e.detail.tower);
  })();
}

function handleGridModeChange(): void {
  canvas.requestRenderAll();
}

function drawGrid(): void {
  const vpt = canvas.viewportTransform;
  const context = htmlCanvas.value?.getContext('2d');
  const width = htmlCanvas.value?.width;
  const height = htmlCanvas.value?.height;
  const zoom = store?.state.towerCanvasState.zoomLevel;

  if (context && vpt && width && height && zoom) {
    CanvasUtils.drawGrid(context, vpt[4], vpt[5], zoom, width, height);
  }
}

function handleSnapToModeChange(): void {
  canvas.requestRenderAll();
}

function handleZoom(zoomLevel?: number): void {
  if (!canvas || !zoomLevel) return;

  const center = canvas.getCenter() ?? { left: 0, top: 0 };
  const centerPoint = new fabric.Point(center.left, center.top);

  canvas.zoomToPoint(centerPoint, zoomLevel);
  canvas.getObjects().forEach((obj) => obj.setCoords());
  canvas.requestRenderAll();
}

function handleLegendModeChange(): void {
  if (!canvas || !canvasState.value) {
    return;
  }

  const enabled = store?.state.towerCanvasState.legendEnabled;
  const legend = canvas.getObjects().find((obj) => obj instanceof fabric.LegendRect);

  if (enabled) {
    if (!legend) {
      startPoint.value.x = 400;
      startPoint.value.y = 300;

      const rect = new fabric.LegendRect({
        left: startPoint.value.x,
        top: startPoint.value.y,
        width: MIN_RECT_SIZE * 6,
        height: MIN_RECT_SIZE * 6,
        originX: 'left',
        originY: 'top',
        angle: 0,
        transparentCorners: false,
        canvas: canvas,
        lockScalingFlip: true,
        textStyle: 'sans-serif',
        strokeWidth: 5,
        strokeDashArray: [7.5, 2.5],
        stroke: '#333333',
        fill: '#4E748B',
      });

      rect.strokeStyle = 'dashed';

      activeRect.value = rect;

      canvas.add(activeRect.value);
      canvas.viewportCenterObject(activeRect.value);
      activeRect.value.setCoords();

      canvas.selection = true;
      activeRect.value.id = uuidv4();

      setLegendLabel(rect);

      canvas.renderAll();
      Commit(StoreMutationType.SET_TOWER_CANVAS_SELECTION_TYPE, 'selection');
    }
  } else {
    canvas.forEachObject((obj: fabric.Object) => {
      if (obj instanceof fabric.LegendRect) {
        canvas.remove(obj);
        canvas.requestRenderAll();
      }
    });
  }
}

function setLegendLabel(legendRect: fabric.LegendRect): void {
  const displayOptions = store?.state.towerCanvasState.displayOptions;
  if (displayOptions) {
    const labelParts: string[] = [];
    const defaultLabels = Array.from(DEFAULT_DISPLAY_LABELS.keys()).filter((e) => e !== Label.AmBestLabel);

    for (const label of defaultLabels) {
      let option = displayOptions[label as keyof DisplayOptions];

      if (option?.enabled) {
        // Carrier and AM Best are displayed on the same line
        if (label === Label.CarrierLabel && displayOptions[Label.AmBestLabel]?.enabled) {
          const carrierLabel = option.customName || (DEFAULT_DISPLAY_LABELS.get(label) ?? '--');
          const amBestLabel = displayOptions[Label.AmBestLabel].customName || (DEFAULT_DISPLAY_LABELS.get(Label.AmBestLabel) ?? '--');
          labelParts.push(`${carrierLabel} (${amBestLabel})`);
        } else {
          labelParts.push(option.customName || (DEFAULT_DISPLAY_LABELS.get(label) ?? '--'));
        }
      }
    }

    legendRect.setLabel(labelParts.join('\n'));
  }
}

// TODO:
// may be a bug with fabricjs, but had to prevent the context menu event from triggering the default behavior.
// this was happening when the style of the context menu was being changed by changes in the vuex store
function handleContextMenu(e: MouseEvent): boolean {
  e.preventDefault();
  e.stopPropagation();
  return false;
}

async function handleUndoTowerHistory(): Promise<void> {
  await undoTowerHistory();
}

async function handleRedoTowerHistory(): Promise<void> {
  await redoTowerHistory();
}

async function handleDownloadTower(fileType: FileType): Promise<void> {
  await DownloadTower(canvas as fabric.Canvas, fileType);
}

function handleUpdateCustomRectPolicy(e: CustomEvent<UpdateTowerCustomPolicyPayload>): void {
  if (!canvas) return;
  if (!e.detail.policyRectId) return;
  for (const obj of canvas.getObjects()) {
    if (obj.id === e.detail.policyRectId && obj.type === CustomPolicyRectType) {
      const customPolicyRect = obj as fabric.CustomPolicyRect;
      customPolicyRect.updatePolicyData(e.detail.policy);
      store?.commit(StoreMutationType.UPDATE_CANVAS_OBJECTS_ARRAY, canvas.getObjects());
      canvas.requestRenderAll();
    }
  }
}

async function handleUpdateTowerVersions(): Promise<void> {
  let towerVersions: ProgramTower[] = [];
  if (store?.state.towerCanvasState.programId) {
    towerVersions = await towerService.getTowers({ programId: store?.state.towerCanvasState.programId });
  } else if (store?.state.towerCanvasState.accountId) {
    towerVersions = await towerService.getTowers({ accountId: store?.state.towerCanvasState.accountId });
  } else {
    towerVersions = [await towerService.getTowerByTowerId(store?.state.towerCanvasState.currentTowerInstance?.towerId ?? '')];
  }

  store?.commit(StoreMutationType.SET_TOWER_VERSIONS, towerVersions);
}

function handleTowerScaleModeChange(e: CustomEvent<TowerScaleMode>): void {
  const obj = canvas.getActiveObject();
  if (obj instanceof fabric.TowerScale) {
    obj.setMode(e.detail);
  }
}

function handleUpdateObjectLayer(e: CustomEvent<ChangeObjectLayerOptions>): void {
  const obj = canvas.getActiveObject();
  if (!obj) return;

  switch (e.detail) {
    case 'send-to-back': {
      obj.sendToBack();
      break;
    }
    case 'send-backward': {
      obj.sendBackwards(true);
      break;
    }
    case 'bring-forward': {
      obj.bringForward(true);
      break;
    }
    case 'bring-to-front': {
      obj.bringToFront();
      break;
    }
  }
  canvas.requestRenderAll();
}

// -------------------
// private functions
// -------------------
/**
 * Saves a reference to an event listener after registering
 */
function registerEventListener(event: string, handler: Handler<any>) {
  addEventListener(event, handler);
  eventHandlers.push([event, handler]);
}

async function saveTower(tower: ProgramTower, action?: SaveTowerAction): Promise<void> {
  await towerService.saveTower({ tower, action });
}

async function updateTower(tower: ProgramTower): Promise<void> {
  await towerService.updateTower(tower);
}

async function deleteTower(tower: ProgramTower): Promise<void> {
  await towerService.deleteTower(tower.towerId);

  const programTowers = await towerService.getTowers({ programId: tower.programId });
  store?.commit(StoreMutationType.SET_TOWER_VERSIONS, programTowers);

  const currentTowerForProgram = getCurrentTowerInstanceForUser(programTowers);
  store?.commit(StoreMutationType.SET_CURRENT_TOWER_INSTANCE, currentTowerForProgram);

  canvas.clear();
}

function applyConfig(obj: fabric.Object, config: TowerObjectConfig): void {
  (config.updatedProps || []).forEach((p) => {
    if (p !== 'type' && isConfigEnabledForObjectType(p, obj.type)) {
      if (p === 'textColor' && obj instanceof fabric.Text) {
        // for fabric.Text objects, the text color is the 'fill' property instead of 'textColor'
        obj.set('fill' as keyof typeof obj, config['textColor' as keyof typeof config]);
      } else if (p === 'width' || p === 'height') {
        // Handle width/height changes by calculating new scale
        const baseWidth = obj.width ?? 1;
        const baseHeight = obj.height ?? 1;
        const currentScaleX = obj.scaleX ?? 1;
        const currentScaleY = obj.scaleY ?? 1;

        if (p === 'width' && config.width !== undefined && baseWidth > 0) {
          const newScaleX = config.width / baseWidth;
          obj.set('scaleX', newScaleX);
          // Fire scaling event to trigger text resizing handlers
          obj.fire('scaling');
        } else if (p === 'height' && config.height !== undefined && baseHeight > 0) {
          const newScaleY = config.height / baseHeight;
          obj.set('scaleY', newScaleY);
          // Fire scaling event to trigger text resizing handlers
          obj.fire('scaling');
        }
      } else {
        obj.set(p as keyof typeof obj, config[p as keyof typeof config]);
      }
    }
  });
}

function panCanvasToObject(obj: fabric.Object): void {
  if (!canvas) return;

  if (canvas.viewportTransform) {
    const vpt = canvas.viewportTransform;

    const scaledCanvasWidth = canvas.getWidth();
    const scaledCanvasHeight = canvas.getHeight();

    vpt[4] = -((obj.left ?? 0) * canvas.getZoom()) + scaledCanvasWidth / 2 - (obj.width ?? 0) / 2;
    vpt[5] = -((obj.top ?? 0) * canvas.getZoom()) + scaledCanvasHeight / 2 - (obj.height ?? 0) / 2;

    canvas.setViewportTransform(canvas.viewportTransform);
    canvas.renderAll();
  }
}

function getTowerPolicyObjectById(id: string): fabric.Object | undefined {
  return canvas.getObjects().find((o) => o.id === id);
}

async function loadCanvasJSON(tower?: ProgramTower, resetTowerHistory?: boolean): Promise<void> {
  if (!canvas) {
    console.error('the canvas is not instantiated');
    return;
  }

  if (resetTowerHistory) {
    Commit(StoreMutationType.RESET_TOWER_HISTORY);
    Commit(StoreMutationType.ADD_TOWER_HISTORY, tower);
  }

  // clear the canvas if the provided tower is undefined
  if (!tower) {
    canvas.clear();
    store?.commit(StoreMutationType.SET_CURRENT_TOWER_INSTANCE, undefined);
    Commit(StoreMutationType.SET_CANVAS_CHANGE_REASON, undefined);
    Commit(StoreMutationType.SET_TOWER_CANVAS_ZOOM_LEVEL, CanvasUtils.DEFAULT_ZOOM);
    Commit(StoreMutationType.SET_GRID_MODE, false);
    return;
  }

  const towerJSON = JSON.parse(tower.towerObject);
  try {
    // Needs to happen before objects are loaded
    if (towerJSON.displayOptions) {
      store?.commit(StoreMutationType.SET_TOWER_DISPLAY_OPTIONS, towerJSON.displayOptions);
    }

    // TODO: watcher attached to this is expecting set_current_tower_instance to be a certain tower
    canvas.loadFromJSON(towerJSON, () => {
      canvas.renderAll.bind(canvas);
      dispatchEvent(new CustomEvent(customEvents.canvasLoaded));
    });

    store?.commit(StoreMutationType.SET_LOAD_ERROR, false);
    store?.commit(StoreMutationType.SET_CURRENT_TOWER_INSTANCE, tower);
    store?.commit(StoreMutationType.UPDATE_CANVAS_OBJECTS_ARRAY, canvas.getObjects());
    store?.commit(StoreMutationType.SET_TOWER_CANVAS_ZOOM_LEVEL, towerJSON.zoomLevel);
    store?.commit(StoreMutationType.SET_GRID_MODE, towerJSON.gridModeEnabled);
    store?.commit(StoreMutationType.SET_SNAP_TO_MODE, towerJSON.snapToModeEnabled);
    store?.commit(StoreMutationType.SET_CUSTOM_POLICIES, towerJSON.customPolicies ?? []);
    store?.commit(StoreMutationType.SET_RENEWAL_PROCESSED, towerJSON.renewalProcessed ?? false);

    canvas.forEachObject((obj: fabric.Object) => {
      if (obj instanceof fabric.LegendRect) {
        store?.commit(StoreMutationType.SET_LEGEND_MODE, true);
      }

      obj.on('editing:exited', () => {
        if (canvasState.value) store?.commit(StoreMutationType.SET_IS_EDITING, false);
      });

      obj.on('editing:entered', () => {
        if (canvasState.value) store?.commit(StoreMutationType.SET_IS_EDITING, true);
      });
    });
  } catch (e) {
    console.error('Error loading tower version', e);

    store?.commit(StoreMutationType.SET_LOAD_ERROR, true);
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to load Tower', color: 'error' });

    tower.loadError = true;
    store?.commit(StoreMutationType.SET_CURRENT_TOWER_INSTANCE, tower);
    await towerService.saveTower({ tower });
  }

  canvas.requestRenderAll();
}

async function processRenewalTower(tower: ProgramTower, programInfo: ProgramInfo) {
  if (!tower.renewalProgramId) return;
  if (!programInfo) return;

  const programPolicies = programInfo.policies;
  const existingCustomPolicies = store?.state.towerCanvasState.customPolicies ?? [];
  const latestCustomPolicyId = existingCustomPolicies?.sort((a, b) => b.id - a.id)[0]?.id ?? 0;

  // Iterate through program policies for each, check if there is an associated policy rect.
  // If there is, convert it to a custom rect
  for (let i = 0; i < programPolicies.length; i++) {
    let policy = programPolicies[i];
    const policyRect = canvas
      .getObjects()
      .filter((obj) => obj.type === PolicyRectType)
      .find((obj) => obj.policy?.id === policy.id);

    // make sure we don't conflict with existing custom policy ids
    policy.id = latestCustomPolicyId + i + 1;
    policy.policyPremium = undefined;

    if (policyRect) {
      const customPolicyRect = new fabric.CustomPolicyRect({
        ...policyRect.toJSON(),
        id: uuidv4(),
        policy: policy,
      } as fabric.PolicyRectOptions);
      canvas.add(customPolicyRect);
      customPolicyRect.updatePolicyData(policy);
      canvas.remove(policyRect);
    }
  }

  store?.commit(StoreMutationType.SET_CUSTOM_POLICIES, [...programPolicies, ...existingCustomPolicies]);

  // convert all remaining policy rects to custom rects that might not have an associated policy
  canvas
    .getObjects()
    .filter((o) => o.type === PolicyRectType)
    .forEach((policyRect) => {
      const policy = {
        ...policyRect.policy,
        id: getNewCustomPolicyId(),
        policyPremium: undefined,
      } as ProgramPolicyItem;

      const customPolicyRect = new fabric.CustomPolicyRect({
        id: uuidv4(),
        ...policyRect.toJSON(),
        policy: policy,
      } as fabric.PolicyRectOptions);
      canvas.add(customPolicyRect);
      customPolicyRect.updatePolicyData(policy);
      canvas.remove(policyRect);
    });

  store?.commit(StoreMutationType.SET_RENEWAL_PROCESSED, true);
}

// Get the most recently updated tower
function getCurrentTowerInstanceForUser(towers: ProgramTower[]): ProgramTower | undefined {
  if (!towers) return;

  const clonedTowers = [...towers].filter((tower) => tower.userId === store?.state?.user?.user?.id);

  return clonedTowers.sort((a, b) => new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime())[0];
}

function applyMovingSnapToLogic(canvas: fabric.Canvas, snapper: fabric.Object): void {
  // Update the snapper's coordinates
  snapper.setCoords();

  // Loop through each object on the canvas
  canvas.forEachObject(function (snappee) {
    // Skip if the snappee is the same as the snapper or should not be snapped
    if (
      snappee === snapper ||
      (snapper.type === 'activeSelection' && (snapper as fabric.ActiveSelection).getObjects().includes(snappee)) ||
      !snappee.snappable
    ) {
      return;
    }

    // Update the snappee's coordinates
    snappee.setCoords();

    // If objects intersect
    if (snapper.isContainedWithinObject(snappee) || snapper.intersectsWithObject(snappee) || snappee.isContainedWithinObject(snapper)) {
      // Adjust position to prevent intersection
      findNewPos(snapper, snappee);
    }

    // Get the corner coordinates of snapper and snappee
    const snapperCorners = snapper.aCoords as fabric.Object['aCoords'];
    const snappeeCorners = snappee.aCoords as fabric.Object['aCoords'];

    if (!snapperCorners || !snappeeCorners) {
      return;
    }

    // List of corner keys to iterate over
    const cornerKeys: Array<keyof typeof snapperCorners> = ['tl', 'tr', 'bl', 'br'];

    // Flag to determine if snapping has occurred
    let snapped = false;

    // Loop through each corner of the snapper
    for (const snapperCornerKey of cornerKeys) {
      const snapperCorner = snapperCorners[snapperCornerKey];

      // Loop through each corner of the snappee
      for (const snappeeCornerKey of cornerKeys) {
        const snappeeCorner = snappeeCorners[snappeeCornerKey];

        // Calculate the distance between the two corners
        const deltaX = snapperCorner.x - snappeeCorner.x;
        const deltaY = snapperCorner.y - snappeeCorner.y;
        const distance = Math.hypot(deltaX, deltaY);

        // Check if the distance is within the snapping threshold
        if (distance <= SNAP_THRESHOLD) {
          // Calculate the offset needed to align the corners
          const offsetX = snappeeCorner.x - snapperCorner.x;
          const offsetY = snappeeCorner.y - snapperCorner.y;

          // Adjust the snapper's position
          snapper.left = (snapper.left ?? 0) + offsetX;
          snapper.top = (snapper.top ?? 0) + offsetY;

          // Update the snapper's coordinates after snapping
          snapper.setCoords();

          // Set the snapped flag to true and break out of the loops
          snapped = true;
          break;
        }
      }
      if (snapped) {
        break;
      }
    }
  });

  // Render the canvas to reflect changes
  canvas.renderAll();
}

function findNewPos(snapper: fabric.Object, snappee: fabric.Object): void {
  // Get the transformed corner coordinates
  const snapperCorners = snapper.aCoords as fabric.Object['aCoords'];
  const snappeeCorners = snappee.aCoords as fabric.Object['aCoords'];

  if (!snapperCorners || !snappeeCorners) {
    return;
  }

  // Get the min and max X and Y values for snapper
  const snapperXValues = [snapperCorners.tl.x, snapperCorners.tr.x, snapperCorners.bl.x, snapperCorners.br.x];
  const snapperYValues = [snapperCorners.tl.y, snapperCorners.tr.y, snapperCorners.bl.y, snapperCorners.br.y];

  const snapperMinX = Math.min(...snapperXValues);
  const snapperMaxX = Math.max(...snapperXValues);
  const snapperMinY = Math.min(...snapperYValues);
  const snapperMaxY = Math.max(...snapperYValues);

  // Get the min and max X and Y values for snappee
  const snappeeXValues = [snappeeCorners.tl.x, snappeeCorners.tr.x, snappeeCorners.bl.x, snappeeCorners.br.x];
  const snappeeYValues = [snappeeCorners.tl.y, snappeeCorners.tr.y, snappeeCorners.bl.y, snappeeCorners.br.y];

  const snappeeMinX = Math.min(...snappeeXValues);
  const snappeeMaxX = Math.max(...snappeeXValues);
  const snappeeMinY = Math.min(...snappeeYValues);
  const snappeeMaxY = Math.max(...snappeeYValues);

  // Calculate overlaps along X and Y axes
  const overlapX = Math.min(snapperMaxX, snappeeMaxX) - Math.max(snapperMinX, snappeeMinX);
  const overlapY = Math.min(snapperMaxY, snappeeMaxY) - Math.max(snapperMinY, snappeeMinY);

  // If there's overlap along both axes, adjust position
  if (overlapX > 0 && overlapY > 0) {
    // Determine the axis with the least overlap
    if (overlapX < overlapY) {
      // Adjust along X axis
      const shiftX = snapperMinX < snappeeMinX ? -overlapX : overlapX;
      snapper.left = (snapper.left ?? 0) + shiftX;
    } else {
      // Adjust along Y axis
      const shiftY = snapperMinY < snappeeMinY ? -overlapY : overlapY;
      snapper.top = (snapper.top ?? 0) + shiftY;
    }

    // Update the snapper's coordinates after adjustment
    snapper.setCoords();
  }
}

function applyScalingSnapToLogic(canvas: fabric.Canvas, snapper: fabric.Object, scalingCorner: string): void {
  // Update the snapper's coordinates
  snapper.setCoords();

  // Get the fixed point (the point opposite to the scaling point)
  const fixedPoint = getFixedPoint(snapper, scalingCorner);
  if (!fixedPoint) {
    return;
  }

  // Get the scaling point (the point being dragged)
  const scalingPoint = getScalingPoint(snapper, scalingCorner);
  if (!scalingPoint) {
    return;
  }

  // Loop through each object on the canvas
  canvas.forEachObject(function (snappee) {
    // Skip if the snappee is the same as the snapper or should not be snapped
    if (
      snappee === snapper ||
      (snapper.type === 'activeSelection' && (snapper as fabric.ActiveSelection).getObjects().includes(snappee)) ||
      !snappee.snappable
    ) {
      return;
    }

    // Update the snappee's coordinates
    snappee.setCoords();

    // Get the corner coordinates of snapper and snappee
    const snapperCorners = snapper.aCoords as fabric.Object['aCoords'];
    const snappeeCorners = snappee.aCoords as fabric.Object['aCoords'];

    if (!snapperCorners || !snappeeCorners) {
      return;
    }

    // List of corner keys to iterate over
    const cornerKeys: Array<keyof typeof snapperCorners> = ['tl', 'tr', 'bl', 'br'];

    // Flag to determine if snapping has occurred
    let snapped = false;

    // Loop through each corner of the snapper
    for (const snapperCornerKey of cornerKeys) {
      // Only consider the corner(s) being scaled
      if (!isEdgePointBeingScaled(snapperCornerKey, scalingCorner)) {
        continue;
      }

      const snapperCorner = snapperCorners[snapperCornerKey];

      // Loop through each corner of the snappee
      for (const snappeeCornerKey of cornerKeys) {
        const snappeeCorner = snappeeCorners[snappeeCornerKey];

        // Calculate the distance between the two corners
        const deltaX = snapperCorner.x - snappeeCorner.x;
        const deltaY = snapperCorner.y - snappeeCorner.y;
        const distance = Math.hypot(deltaX, deltaY);

        // Check if the distance is within the snapping threshold
        if (distance <= SNAP_THRESHOLD) {
          // Adjust the scaling to align the corner
          adjustScalingToSnapEdge(snapper, scalingCorner, scalingPoint, snappeeCorner, fixedPoint);

          // Update the snapper's coordinates after snapping
          snapper.setCoords();

          // Set the snapped flag to true and break out of the loops
          snapped = true;
          break;
        }
      }
      if (snapped) {
        break;
      }
    }
  });

  // Render the canvas to reflect changes
  canvas.renderAll();
}

// Function to adjust scaling to snap the edge
function adjustScalingToSnapEdge(
  snapper: fabric.Object,
  scalingCorner: string,
  scalingPoint: fabric.Point,
  snappeePoint: fabric.Point,
  fixedPoint: fabric.Point,
): void {
  // Calculate the vector from fixed point to scaling point
  const originalVector = new fabric.Point(scalingPoint.x - fixedPoint.x, scalingPoint.y - fixedPoint.y);

  // Calculate the desired vector from fixed point to snappee point
  const desiredVector = new fabric.Point(snappeePoint.x - fixedPoint.x, snappeePoint.y - fixedPoint.y);

  // Get the object's current angle in radians
  const angle = (snapper.angle || 0) * (Math.PI / 180);

  // Rotate vectors into the object's local coordinate system
  const originalVectorLocal = rotateVector(originalVector, -angle);
  const desiredVectorLocal = rotateVector(desiredVector, -angle);

  // Calculate scaling factors in local coordinate system
  let scaleXFactor = 1;
  let scaleYFactor = 1;

  if (['ml', 'mr'].includes(scalingCorner) && originalVectorLocal.x !== 0) {
    scaleXFactor = desiredVectorLocal.x / originalVectorLocal.x;
  }
  if (['mt', 'mb'].includes(scalingCorner) && originalVectorLocal.y !== 0) {
    scaleYFactor = desiredVectorLocal.y / originalVectorLocal.y;
  }

  // Apply scaling factors, ensuring we don't flip the object
  if (scaleXFactor > 0 && !isNaN(scaleXFactor) && isFinite(scaleXFactor)) {
    snapper.scaleX = snapper.scaleX! * scaleXFactor;
  }
  if (scaleYFactor > 0 && !isNaN(scaleYFactor) && isFinite(scaleYFactor)) {
    snapper.scaleY = snapper.scaleY! * scaleYFactor;
  }

  // Update the snapper's coordinates after scaling
  snapper.setCoords();

  // Adjust position to keep the fixed point stationary
  // Get the new position of the fixed point after scaling
  const newFixedPoint = getFixedPoint(snapper, scalingCorner);
  if (!newFixedPoint) {
    return;
  }

  // Calculate the difference between the fixed point's position before and after scaling
  const deltaX = fixedPoint.x - newFixedPoint.x;
  const deltaY = fixedPoint.y - newFixedPoint.y;

  // Adjust the object's position
  snapper.left = (snapper.left ?? 0) + deltaX;
  snapper.top = (snapper.top ?? 0) + deltaY;

  // Update coordinates after position adjustment
  snapper.setCoords();
}

// Function to determine if a corner is being scaled
function isEdgePointBeingScaled(cornerKey: string, scalingCorner: string): boolean {
  // Map scaling corner to corresponding corners
  const scalingCornersMap: { [key: string]: string[] } = {
    ml: ['tl', 'bl'],
    mr: ['tr', 'br'],
    mt: ['tl', 'tr'],
    mb: ['bl', 'br'],
  };

  return scalingCornersMap[scalingCorner]?.includes(cornerKey) ?? false;
}

// Function to get the fixed point opposite to the scaling point
function getFixedPoint(obj: fabric.Object, scalingCorner: string): fabric.Point | null {
  switch (scalingCorner) {
    case 'ml': // Left edge, fixed point is right middle
      return obj.getPointByOrigin('right', 'center');
    case 'mr': // Right edge, fixed point is left middle
      return obj.getPointByOrigin('left', 'center');
    case 'mt': // Top edge, fixed point is center bottom
      return obj.getPointByOrigin('center', 'bottom');
    case 'mb': // Bottom edge, fixed point is center top
      return obj.getPointByOrigin('center', 'top');
    default:
      return null;
  }
}

// Function to get the scaling point (the point being dragged)
function getScalingPoint(obj: fabric.Object, scalingCorner: string): fabric.Point | null {
  switch (scalingCorner) {
    case 'ml': // Left edge, scaling point is left middle
      return obj.getPointByOrigin('left', 'center');
    case 'mr': // Right edge, scaling point is right middle
      return obj.getPointByOrigin('right', 'center');
    case 'mt': // Top edge, scaling point is center top
      return obj.getPointByOrigin('center', 'top');
    case 'mb': // Bottom edge, scaling point is center bottom
      return obj.getPointByOrigin('center', 'bottom');
    default:
      return null;
  }
}

// Function to rotate a vector by a given angle
function rotateVector(vector: fabric.Point, angle: number): fabric.Point {
  const cosTheta = Math.cos(angle);
  const sinTheta = Math.sin(angle);
  return new fabric.Point(vector.x * cosTheta - vector.y * sinTheta, vector.x * sinTheta + vector.y * cosTheta);
}
</script>

<template>
  <canvas
    id="c"
    ref="htmlCanvas"
    class="tower__canvas"
    :style="{
      width: `${canvasDOMWidth}px`,
      height: `${canvasDOMHeight}px`,
    }"
    :width="canvasDOMWidth"
    :height="canvasDOMHeight">
  </canvas>
</template>

<style>
.tower__canvas {
  z-index: var(--zindex-canvas);
}
</style>
